import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ReportBugData } from "@/domain/entities/vistassistant/TicketTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ConversationBubble from "./ConversationBubble";

type ReportBugResponseProps = {
    data: ReportBugData;
    onSubmit: (data: ReportBugData) => Promise<void>;
};

const ReportBugResponse = ({ data, onSubmit }: ReportBugResponseProps) => {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await onSubmit(data);
            setSubmitted(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <ConversationBubble>
                <p className="text-sm text-green-600">
                    {t("vistassistant.reportBug.submitted")}
                </p>
            </ConversationBubble>
        );
    }

    return (
        <ConversationBubble>
            <div className="flex flex-col gap-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                    <Badge className="bg-red-500 text-white">
                        {t("vistassistant.reportBug.badge")}
                    </Badge>
                    {data.isCritical && (
                        <Badge className="bg-red-700 text-white">
                            {t("vistassistant.reportBug.critical")}
                        </Badge>
                    )}
                    <Badge className="border border-border bg-transparent text-foreground">
                        {data.bugType}
                    </Badge>
                    <Badge className="border border-border bg-transparent text-foreground">
                        {data.feature}
                    </Badge>
                    {data.aiConfidence === "partial" && (
                        <Badge className="bg-muted text-muted-foreground">
                            {t("vistassistant.reportBug.partial")}
                        </Badge>
                    )}
                </div>

                <p className="font-medium">{data.title}</p>

                {data.description && (
                    <p className="text-muted-foreground">{data.description}</p>
                )}

                {data.context && (
                    <p className="text-xs italic text-muted-foreground">
                        {t("vistassistant.reportBug.context")}: {data.context}
                    </p>
                )}

                {data.similarTickets && data.similarTickets.length > 0 && (
                    <div className="rounded border border-yellow-200 bg-yellow-50 p-2 dark:border-yellow-800 dark:bg-yellow-950">
                        <p className="mb-1 text-xs font-medium text-yellow-700 dark:text-yellow-300">
                            {t("vistassistant.reportBug.similarTickets", {
                                count: data.similarTickets.length,
                            })}
                        </p>
                        {data.similarTickets.map((ticket) => (
                            <p
                                key={ticket.id}
                                className="text-xs text-yellow-600 dark:text-yellow-400"
                            >
                                • {ticket.title}{" "}
                                {ticket.votes !== undefined &&
                                    `(${ticket.votes} votes)`}
                            </p>
                        ))}
                    </div>
                )}

                <Button
                    className="w-fit bg-red-500 hover:bg-red-600"
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                    size="sm"
                >
                    {isSubmitting
                        ? t("vistassistant.reportBug.submitting")
                        : t("vistassistant.reportBug.submit")}
                </Button>
            </div>
        </ConversationBubble>
    );
};

export default ReportBugResponse;
